import { FC, useEffect, useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AppProvider } from './app/AppProvider';
import { RequireAuth } from './app/RequireAuth';
import { ReadOnlyBanner } from './app/ReadOnlyBanner';
import { RenderManager } from './app/RenderManager';
import EditorPage from './pages/EditorPage';
import DashboardPage from './pages/DashboardPage';
import StartPage from './pages/StartPage';
import AdminMetricsPage from './pages/AdminMetricsPage';
import InvitePage from './pages/invite/InvitePage';
import ConfirmPage from './pages/auth/ConfirmPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import { CommandMenu } from './widgets/CommandMenu';
import { SyncStatusBadge } from './features/sync';
import { PlanBadge, UpgradePromptModal, useUpgradeModal } from './features/billing';
import { PostExportPrompt } from './features/referral';
import { SessionRevokedOverlay } from './features/auth';
import { PwaUpdatePrompt } from './features/pwa-update';
import { useAuthStore } from './stores/useAuthStore';
import { migrateIdbToZustand } from './stores/migrate';
import { useProjectStore } from './stores/useProjectStore';

const App: FC = () => {
  const [ready, setReady] = useState(false);
  const { plan, planStatus, rewardDaysRemaining, readOnlyPreview } = useAuthStore();
  const { open: upgradeOpen, feature: upgradeFeature, close: closeUpgrade } = useUpgradeModal();

  useEffect(() => {
    // Boot migration chain — ORDER IS CRITICAL:
    // 1. migrateIdbToZustand: move old IDB data to localStorage
    // 2. explicit project-store rehydrate: now that IndexedDB data has landed in localStorage,
    //    hydrate the persisted project store (SAFE-05). The store declares skipHydration:true so
    //    it does NOT auto-hydrate at module-evaluation time — that used to race ahead of step 1
    //    and run the whole session on pre-migration values. Its persist `migrate` option
    //    (migrateProjectStore) backfills schema fields (e.g. nameIsManual) as part of this call,
    //    replacing the old post-hoc raw-blob migration.
    // 3. migrateLocalToCloud (plan 04-04): upload local projects to server
    // Step 2 must run AFTER step 1; step 3 must run AFTER step 2.
    migrateIdbToZustand()
      .then(() => useProjectStore.persist.rehydrate())
      .finally(() => setReady(true));
  }, []);

  if (!ready) return null;

  return (
    <AppProvider>
      {/* Phase 25: /invite/:code is hoisted ABOVE RequireAuth (CRITICAL — Pitfall 1).
          RequireAuth renders LandingPage for unauthenticated users, so any route inside it
          would never mount for a visitor arriving via an invite link — the sessionStorage
          write would never fire and attribution would be lost.
          InvitePage renders no protected content: it only writes to sessionStorage and redirects.
          ACCESS-01: RequireAuth wraps the /* subtree — anonymous users see LandingPage, expired
          users see PaywallPage, in place (no URL change). AuthInit above {children} in AppProvider
          ensures migrateLocalToCloud fires before the guard renders any page. */}
      {/* WR-04: when the read-only soft-lock preview is active, the fixed ReadOnlyBanner
          (mounted below) sits at top:0. Shift the routed page content down by the banner
          height so the banner never overlaps the page navbar. */}
      <div className={readOnlyPreview ? 'pt-10' : undefined}>
      <Routes>
        <Route path="/invite/:code" element={<InvitePage />} />
        {/* Magic-link confirmation interstitial — hoisted ABOVE RequireAuth (like /invite).
            Email links point here (inert to link-scanner prefetch) instead of directly at
            the one-time verify endpoint; see src/pages/auth/ConfirmPage.tsx. */}
        <Route path="/auth/confirm" element={<ConfirmPage />} />
        {/* Phase 43: /auth/reset — same hoisting rationale as /auth/confirm above: an
            anonymous visitor arriving from a reset-email link must reach this page;
            RequireAuth would otherwise substitute LandingPage in its place. */}
        <Route path="/auth/reset" element={<ResetPasswordPage />} />
        <Route
          path="/*"
          element={
            <RequireAuth>
              <Routes>
                <Route path="/" element={<StartPage />} />
                <Route path="/editor" element={<EditorPage />} />
                <Route path="/dashboard" element={<DashboardPage />} />
                <Route path="/admin" element={<AdminMetricsPage />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </RequireAuth>
          }
        />
      </Routes>
      </div>
      <CommandMenu />
      <SyncStatusBadge />
      {/* REFUI-05: PostExportPrompt — one-time global toast, localStorage-guarded */}
      <PostExportPrompt />
      <PlanBadge plan={plan} planStatus={planStatus ?? undefined} rewardDaysRemaining={rewardDaysRemaining ?? undefined} />
      <UpgradePromptModal
        open={upgradeOpen}
        onOpenChange={(o) => !o && closeUpgrade()}
        feature={upgradeFeature}
      />
      {/* WR-04: Sticky read-only banner shown when user is in soft-lock preview mode.
          Mounts outside RequireAuth so it overlays the app content. */}
      <ReadOnlyBanner />
      {/* Phase 43 (PWD-06): global blocking notice for a tab whose session was just killed
          by a password reset/change made elsewhere. Global level, outside RequireAuth,
          sibling of ReadOnlyBanner/PwaUpdatePrompt — must reach whichever route the tab
          has open, including /editor. */}
      <SessionRevokedOverlay />
      {/* PWA: «Доступно обновление» баннер — обновляет устаревшую веб-версию по клику */}
      <PwaUpdatePrompt />
      <RenderManager />
    </AppProvider>
  );
}

export default App;